import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Download, Lock, FileText, HelpCircle, Clock, ListChecks } from "lucide-react";
import api from "@/lib/api";
import { downloadTemplate } from "@/lib/download";
import { Overline, Loading, LockCard } from "@/components/shared";

export default function TemplateDetail() {
  const { slug } = useParams();
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setT(null); window.scrollTo(0, 0); api.get(`/content/templates/${slug}`).then((r) => setT(r.data)); }, [slug]);
  if (!t) return <Loading />;

  const handleDownload = async () => { setBusy(true); await downloadTemplate(t.slug, t.title); setBusy(false); };

  return (
    <div className="max-w-3xl" data-testid="template-detail">
      <Link to="/app/templates" className="inline-flex items-center gap-1.5 text-sm text-nm-neutral hover:text-nm-primary mb-4 transition-colors" data-testid="tpl-back">
        <ArrowLeft size={15} /> Template Library
      </Link>
      <Overline>{t.category} · Template</Overline>
      <h1 className="font-serif text-4xl sm:text-5xl text-nm-ink mt-2 leading-tight">{t.title}</h1>
      <p className="text-xl text-nm-neutral font-light leading-relaxed mt-4">{t.summary}</p>

      <div className="flex flex-wrap items-center gap-3 mt-7">
        {t.unlocked ? (
          <button onClick={handleDownload} disabled={busy} data-testid="detail-download-btn"
            className="inline-flex items-center gap-2 rounded-full bg-nm-coral px-6 py-3 text-sm font-medium text-white hover:brightness-105 disabled:opacity-60 transition-[filter] duration-200">
            <Download size={17} /> {busy ? "Preparing…" : "Download PDF"}
          </button>
        ) : (
          <Link to="/app/account#upgrade" data-testid="detail-unlock-btn"
            className="inline-flex items-center gap-2 rounded-full bg-nm-primary px-6 py-3 text-sm font-medium text-white hover:brightness-105 transition-[filter] duration-200">
            <Lock size={16} /> Unlock to download
          </Link>
        )}
        {t.related_chapter_meta && (
          <Link to={`/app/chapter/${t.related_chapter_meta.slug}`} data-testid="detail-related-chapter"
            className="inline-flex items-center gap-2 rounded-full border border-nm-primary/15 px-6 py-3 text-sm font-medium text-nm-primary hover:bg-nm-primary/5 transition-colors duration-200">
            View related chapter <ArrowRight size={15} />
          </Link>
        )}
      </div>

      {!t.unlocked && (
        <div className="mt-8"><LockCard part={t.part} teaser="This template unlocks with its part. You can preview its full structure below." /></div>
      )}

      <div className="mt-10 space-y-8">
        <DetailBlock icon={HelpCircle} title="What this helps with">{t.summary}</DetailBlock>
        <DetailBlock icon={Clock} title="When to use it">{t.when_to_use}</DetailBlock>
        <div>
          <div className="flex items-center gap-2 text-nm-primary font-medium text-sm uppercase tracking-wide mb-3"><ListChecks size={16} /> What to think about before using it</div>
          <ul className="space-y-2.5">
            {t.think_about_before.map((q, i) => (
              <li key={i} className="flex items-start gap-3 text-nm-ink/85 leading-relaxed">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-nm-primary shrink-0" />{q}
              </li>
            ))}
          </ul>
        </div>

        {/* Preview fields */}
        <div>
          <div className="flex items-center gap-2 text-nm-primary font-medium text-sm uppercase tracking-wide mb-3"><FileText size={16} /> Preview: fields & structure</div>
          <div className="rounded-2xl border border-nm-primary/12 overflow-hidden nm-shadow-sm">
            {t.preview_fields.map((f, i) => (
              <div key={i} className={`flex flex-col sm:flex-row gap-1 sm:gap-4 p-4 ${i % 2 ? "bg-nm-bg" : "bg-white"} ${i > 0 ? "border-t border-nm-primary/8" : ""}`} data-testid={`field-${i}`}>
                <div className="sm:w-2/5 font-medium text-nm-ink text-sm">{f.label}</div>
                <div className="sm:w-3/5 text-sm text-nm-neutral">{f.hint || <span className="italic text-nm-neutral/50">Your notes…</span>}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-nm-highlight/30 bg-gradient-to-b from-[#FCF6E4] to-[#FFFDF8] p-6">
          <div className="text-nm-ink font-medium mb-1.5">Example use</div>
          <p className="text-nm-ink/80 leading-relaxed italic">{t.example_use}</p>
        </div>
      </div>
    </div>
  );
}

function DetailBlock({ icon: Icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-nm-primary font-medium text-sm uppercase tracking-wide"><Icon size={16} /> {title}</div>
      <p className="text-nm-ink/85 leading-relaxed mt-2.5 text-[17px]">{children}</p>
    </div>
  );
}
